import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { REQUETE_DE_FOND } from '../interceptors/error.interceptor';
import { NotificationFamilleCode } from '../notifications/notification-famille';

/** Notification renvoyée par le backend (centre de notifications, /api/me/notifications). */
export interface NotificationView {
  id: string;
  type: string;
  /** Code de famille (regroupement), déduit du type par le serveur. */
  famille: NotificationFamilleCode | string;
  title: { ar?: string; fr?: string } | null;
  body: { ar?: string; fr?: string } | null;
  link: string | null;
  /** ISO-8601 avec décalage horaire. */
  createdAt: string | null;
  unread: boolean;
}

/**
 * Une page de notifications. `unread` est GLOBAL (tous filtres levés) : c'est le chiffre de la cloche ;
 * `total` suit les filtres demandés : c'est le chiffre de la pagination.
 */
export interface NotificationPage {
  items: NotificationView[];
  total: number;
  unread: number;
  page: number;
  size: number;
}

export interface NotificationQuery {
  page?: number;
  size?: number;
  unread?: boolean;
  famille?: NotificationFamilleCode | null;
}

/**
 * Accès « mes notifications » (cloche et page « كل الإشعارات »). Le Bearer OIDC est posé par
 * l'intercepteur ; apiBaseUrl = '/api' relatif (proxy, même origine). Le périmètre (mon identité / mes
 * rôles) est garanti côté serveur, qui fusionne aussi les doublons multi-rôles et compte les non-lues.
 */
@Injectable({ providedIn: 'root' })
export class NotificationApi {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/me/notifications`;

  /**
   * LA LISTE DU PANNEAU EST UN POLL (60 s, depuis le layout) : requête de FOND. Son 401 se répare en
   * silence et ne navigue jamais — c'est lui qui faisait « recharger la page toute seule » (staging,
   * 2026-09-01). Les gestes (lire, masquer), eux, restent des actions de l'agent.
   */
  page(q: NotificationQuery = {}, deFond = true): Observable<NotificationPage> {
    let params = new HttpParams()
      .set('page', String(q.page ?? 0))
      .set('size', String(q.size ?? 30));
    if (q.unread) {
      params = params.set('unread', 'true');
    }
    if (q.famille) {
      params = params.set('famille', q.famille);
    }
    const context = deFond ? new HttpContext().set(REQUETE_DE_FOND, true) : undefined;
    return this.http.get<NotificationPage>(this.base, { params, context });
  }

  /** Le seul chiffre de la cloche (sondage léger). */
  unreadCount(): Observable<{ count: number }> {
    return this.http.get<{ count: number }>(`${this.base}/unread-count`, {
      context: new HttpContext().set(REQUETE_DE_FOND, true),
    });
  }

  markRead(id: string): Observable<void> {
    return this.http.post<void>(`${this.base}/${id}/read`, {});
  }

  markAllRead(): Observable<void> {
    return this.http.post<void>(`${this.base}/read-all`, {});
  }

  /** Masque pour moi une notification (elle survit pour les autres porteurs d'un rôle). */
  hide(id: string): Observable<void> {
    return this.http.post<void>(`${this.base}/${id}/hide`, {});
  }

  /** « مسح الكل » : masque pour moi tout ce qui est visible, côté serveur. */
  hideAll(): Observable<void> {
    return this.http.post<void>(`${this.base}/hide-all`, {});
  }
}
